<template>
  <div>
    <div
      class="d-flex flex-column flex-sm-row justify-content-sm-between gap-2"
    >
      <div>
        <label class="form-label mb-1">
          <strong>Pinpoint Site Location</strong>
        </label>
        <p class="mb-2 text-muted">
          Move the map, then click where the site is to drop the pin.
        </p>
      </div>
      <div class="d-flex align-items-start justify-content-sm-end">
        <button
          v-if="hasCoordinates"
          type="button"
          class="btn btn-sm btn-alt-secondary"
          @click="clearLocation"
        >
          Clear Pin
        </button>
      </div>
    </div>

    <div v-if="apiKey" class="border rounded overflow-hidden bg-body-secondary">
      <div ref="mapElement" :style="{ width: '100%', height }"></div>
    </div>
    <div v-else class="alert alert-warning mb-0">
      Google Maps is not configured, so the location pin cannot be set here.
    </div>

    <div class="row g-2 mt-1 text-muted small">
      <div class="col-sm-6">
        Latitude: {{ formatCoordinate(modelValue?.latitude) }}
      </div>
      <div class="col-sm-6">
        Longitude: {{ formatCoordinate(modelValue?.longitude) }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const defaultCenter = {
  lat: 53.230553360548186,
  lng: -0.5408974548965823,
};

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      latitude: null,
      longitude: null,
    }),
  },
  height: {
    type: String,
    default: "320px",
  },
});

const emit = defineEmits(["update:modelValue"]);

const apiKey = window.googleMapsApiKey;
const mapId = window.googleMapsMapId || undefined;

const mapElement = ref(null);
const map = ref(null);
const marker = ref(null);
const googleMaps = ref(null);
const mapClickListener = ref(null);
const markerDragListener = ref(null);

const hasCoordinates = computed(
  () => normalizeCoordinates(props.modelValue) !== null
);

function formatCoordinate(value) {
  if (value === null || value === undefined || value === "") {
    return "Not set";
  }

  return Number(value).toFixed(6);
}

function normalizeCoordinates(coords) {
  const rawLatitude = coords?.latitude;
  const rawLongitude = coords?.longitude;

  if (
    rawLatitude === null ||
    rawLatitude === undefined ||
    rawLatitude === "" ||
    rawLongitude === null ||
    rawLongitude === undefined ||
    rawLongitude === ""
  ) {
    return null;
  }

  const latitude = Number(rawLatitude);
  const longitude = Number(rawLongitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    lat: Number(latitude.toFixed(6)),
    lng: Number(longitude.toFixed(6)),
  };
}

function emitCoordinates(position) {
  emit("update:modelValue", {
    latitude: position === null ? null : position.lat,
    longitude: position === null ? null : position.lng,
  });
}

function removeMarker() {
  if (marker.value) {
    marker.value.setMap(null);
    marker.value = null;
  }

  if (markerDragListener.value) {
    markerDragListener.value.remove();
    markerDragListener.value = null;
  }
}

function ensureMarker() {
  if (!googleMaps.value || !map.value || marker.value) {
    return;
  }

  marker.value = new googleMaps.value.maps.Marker({
    map: map.value,
    draggable: true,
  });

  markerDragListener.value = marker.value.addListener("dragend", (event) => {
    if (!event.latLng) {
      return;
    }

    updateMarker(event.latLng.toJSON(), false);
  });
}

function updateMarker(position, shouldPan = true) {
  const normalizedPosition = normalizeCoordinates({
    latitude: position?.lat,
    longitude: position?.lng,
  });

  if (!normalizedPosition || !map.value) {
    return;
  }

  ensureMarker();
  marker.value.setPosition(normalizedPosition);

  if (shouldPan) {
    map.value.panTo(normalizedPosition);
  }

  if ((map.value.getZoom() ?? 0) < 15) {
    map.value.setZoom(15);
  }

  emitCoordinates(normalizedPosition);
}

async function loadGoogleMaps() {
  if (window.google?.maps) {
    return window.google;
  }

  if (window.googleMapsLoaderPromise) {
    return window.googleMapsLoaderPromise;
  }

  window.googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return window.googleMapsLoaderPromise;
}

function clearLocation() {
  removeMarker();
  emitCoordinates(null);
}

async function initialiseMap() {
  if (!apiKey || !mapElement.value || map.value) {
    return;
  }

  googleMaps.value = await loadGoogleMaps();

  const initialPosition =
    normalizeCoordinates(props.modelValue) ?? defaultCenter;

  map.value = new googleMaps.value.maps.Map(mapElement.value, {
    center: initialPosition,
    zoom: normalizeCoordinates(props.modelValue) ? 15 : 9,
    mapId,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
  });

  mapClickListener.value = map.value.addListener("click", (event) => {
    if (!event.latLng) {
      return;
    }

    updateMarker(event.latLng.toJSON(), false);
  });

  if (normalizeCoordinates(props.modelValue)) {
    updateMarker(initialPosition, false);
  }
}

watch(
  () => [props.modelValue?.latitude, props.modelValue?.longitude],
  ([latitude, longitude]) => {
    const normalizedPosition = normalizeCoordinates({
      latitude,
      longitude,
    });

    if (!map.value) {
      return;
    }

    if (normalizedPosition === null) {
      removeMarker();
      return;
    }

    const currentPosition = marker.value?.getPosition();
    const currentLatitude = currentPosition?.lat();
    const currentLongitude = currentPosition?.lng();

    if (
      currentLatitude === normalizedPosition.lat &&
      currentLongitude === normalizedPosition.lng
    ) {
      return;
    }

    updateMarker(normalizedPosition);
  }
);

onMounted(() => {
  initialiseMap();
});

onBeforeUnmount(() => {
  if (mapClickListener.value) {
    mapClickListener.value.remove();
  }

  removeMarker();
});
</script>
